import React from 'react';
import Logo from '../Branding/Logo'; // Import the Logo component

/**
 * QuestionLayout provides a consistent structure for question screens.
 * It includes areas for a header (logo, progress), question content (title, image, description),
 * answer options, and navigation.
 */
const QuestionLayout = React.forwardRef(({
    withLogo,          // JSX for the logo (e.g., <div className="logo-checkin">...</div>)
    progressText,           // String for progress (e.g., "Paso 1 de 5")
    questionTitle,          // String: The main question text
    questionDescription,    // Optional String: Additional context for the question
    questionImageComponent, // Optional JSX: An image or illustration related to the question
    answerOptionsComponent, // JSX: The component rendering the actual answer inputs
    onNext,                 // Function: Handler for the "Next" button click
    onPrevious,             // Function: Handler for the "Previous" button click
    onSkip,                 // Optional Function: Handler for the "Skip" button click
    isNextDisabled = false,
    isPreviousDisabled = false,
    isSkipDisabled = false,
    isLoading = false,
    nextButtonText = "Siguiente",
    previousButtonText = "Anterior",
    skipButtonText = "Omitir",
    formContainerClassName = "form-container text-center bg-white p-4 p-md-5 shadow-sm rounded", // New prop
    contentColumnClassName = "col-md-8 form-side m-auto", // New prop with default value
    style // Add style prop
}, ref) => { // Add ref as the second argument
    return (
        <div
            ref={ref} // Apply the forwarded ref to the root element
            className="container-fluid flex-grow-1 d-flex flex-column question-page-wrapper" // CSSTransition will add its classes here if not explicitly handled by props.className
            style={{
                ...style, // Apply passed-in styles for animation
                paddingTop: '70px', // Adjust this value to the actual height of your StepsNav
                // Removing p-0 to allow this paddingTop to take effect.
                boxSizing: 'border-box' // Ensures padding is included within the element's total width and height
            }}
        >
            {/* Header: Only render if progressText is provided */}
            {/* This header is part of QuestionLayout, distinct from StepsNav */}
            {progressText && (
                <header className="question-header d-flex justify-content-end align-items-center py-2 px-3 shadow-sm bg-light border-bottom">
                    <div className="progress-indicator fw-medium">{progressText}</div>
                </header>
            )}

            {/* Main Content Area */}
            {/* flex-grow-1 allows this main area to expand and fill available vertical space */}
            {/* This row will take the available vertical space (due to flex-grow-1) and center its content (the col-md-8) */}
            <div className="row flex-grow-1 align-items-center justify-content-center">
                <div className={contentColumnClassName}> {/* Use the new prop here */}
                    {/* This div now acts as the 'form-container' from the HTML, with card-like styling */}
                    <div className={formContainerClassName} style={{ width: '100%' }}>
                        {/* Render the Logo component here, if withLogo is true. */}
                        {/* The 'form-container' is text-center. Specific logo classes from HTML are applied here. */}
                        {withLogo && (
                            <div className="logo-checkin mb-3 text-center"> {/* Classes from HTML */}
                                <Logo />
                            </div>
                        )}
                        {questionImageComponent && (
                            <div className="question-image-area text-center mb-4">
                                {questionImageComponent}
                            </div>
                        )}
                        <div className="question-text-area"> {/* text-center is inherited from form-container */}
                            {questionTitle && <h3 className="question-title fw-bold mb-4 text-center">{questionTitle}</h3>} {/* Matched HTML classes and margin */}
                            {questionDescription && <h5 className="question-sub title text-muted mb-5 text-center">{questionDescription}</h5>} {/* Changed to h5, matched HTML classes and margin, kept text-muted */}
                        </div>
                        <div className="answer-options-area">
                            {answerOptionsComponent}
                        </div>

                        {/* Navigation Buttons - Moved Here */}
                        <div className="buttons-container d-grid gap-3 mt-5 mb-3">
                            {/* Continuar/Next Button */}
                            <button
                                type="button"
                                className="btn btn-checkin" // Matched class from HTML
                                onClick={onNext}
                                disabled={isNextDisabled || isLoading}
                            >
                                {isLoading ? 'Cargando...' : nextButtonText}
                            </button>
                            {/* Atrás/Previous Button */}
                            <button
                                type="button"
                                className="btn btn-back" // Matched class from HTML
                                onClick={onPrevious}
                                disabled={isPreviousDisabled || isLoading}
                            >
                                {previousButtonText}
                            </button>
                            {/* Optional Skip Button - Placed below main buttons for consistency if used */}
                            {onSkip && (
                                <button
                                    type="button"
                                    className="btn btn-link text-secondary mt-2" // Adjusted styling for skip
                                    onClick={onSkip}
                                    disabled={isSkipDisabled || isLoading}
                                >
                                    {skipButtonText}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
                {/* The empty footer was removed as buttons are now in the card */}
            </div>
        </div> // This closes the root div (the one with ref={ref})
    );
});

export default QuestionLayout;
